import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  Tooltip,
  Fab,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Switch,
  FormGroup,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  MedicalServices as MedicalIcon,
  History as HistoryIcon,
  LocalHospital as HospitalIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Timeline as TimelineIcon,
  Medication as MedicationIcon,
  Receipt as ReceiptIcon,
  Telegram as TelegramIcon,
  HealthAndSafety as ChronicIcon,
  Send as SendIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { patientsApi, prescriptionsApi } from "../services/api";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

const PatientManagementPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [patientHistory, setPatientHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [patientForm, setPatientForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    isChronic: false,
    chronicCondition: "",
    smsConsent: false,
    emailConsent: false,
    telegramConsent: false,
    telegramUsername: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Ensure patients is always an array
  const patientsArray = Array.isArray(patients) ? patients : [];

  // Calculate statistics safely
  const statistics = {
    totalPatients: patientsArray.length,
    chronicPatients: patientsArray.filter(p => p.isChronic).length,
    withTelegram: patientsArray.filter(p => p.telegramConsent && p.telegramUsername).length,
    withSmsConsent: patientsArray.filter(p => p.smsConsent).length,
  };

  // Fetch all patients
  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await patientsApi.getAll();

      // Handle different response structures
      let patientsData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          patientsData = response.data;
        } else if (response.data.patients && Array.isArray(response.data.patients)) {
          patientsData = response.data.patients;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          patientsData = response.data.data;
        }
      }

      console.log("Fetched patients data:", patientsData);
      setPatients(patientsData);
      setError("");
    } catch (err) {
      console.error("Failed to fetch patients:", err);
      setError(err.response?.data?.message || t("common.failedToLoad"));
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  // Search patients
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchPatients();
      return;
    }

    try {
      setSearchLoading(true);
      const filtered = patientsArray.filter(patient =>
        (patient.fullName && patient.fullName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (patient.phone && patient.phone.includes(searchQuery)) ||
        (patient.email && patient.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (patient.telegramUsername && patient.telegramUsername.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setPatients(filtered);
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Fetch patient prescriptions history
  const fetchPatientHistory = async (patientId) => {
    try {
      setHistoryLoading(true);
      if (!selectedPatient) return;

      const response = await prescriptionsApi.search({
        name: selectedPatient?.fullName,
        phone: selectedPatient?.phone
      });

      // Handle response structure
      let historyData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          historyData = response.data;
        } else if (response.data.prescriptions && Array.isArray(response.data.prescriptions)) {
          historyData = response.data.prescriptions;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          historyData = response.data.data;
        }
      }

      setPatientHistory(historyData);
    } catch (err) {
      console.error("Failed to fetch patient history:", err);
      setPatientHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setPatientForm({
        ...patientForm,
        [name]: checked,
      });
    } else {
      setPatientForm({
        ...patientForm,
        [name]: value,
      });
    }

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  // Validate form according to your model
  const validateForm = () => {
    const errors = {};

    if (!patientForm.fullName.trim()) {
      errors.fullName = t("patient.fullNameRequired");
    }

    if (!patientForm.phone.trim()) {
      errors.phone = t("patient.phoneRequired");
    } else if (!/^[+]?[0-9\s\-()]{9,15}$/.test(patientForm.phone)) {
      errors.phone = t("patient.invalidPhone");
    }

    if (patientForm.email && !/\S+@\S+\.\S+/.test(patientForm.email)) {
      errors.email = t("patient.invalidEmail");
    }

    if (patientForm.telegramUsername && !/^@[a-zA-Z0-9_]{5,32}$/.test(patientForm.telegramUsername)) {
      errors.telegramUsername = t("patient.invalidTelegram");
    }

    if (patientForm.isChronic && !patientForm.chronicCondition.trim()) {
      errors.chronicCondition = t("patient.chronicConditionRequired");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const patientData = {
        ...patientForm,
        registeredById: user?.id, // Add current user ID
      };

      if (selectedPatient) {
        // Update existing patient
        await patientsApi.update(selectedPatient.id, patientData);
        setSuccess(t("patient.updatedSuccessfully"));
        toast.success(t("patient.updatedSuccessfully"));
      } else {
        // Create new patient
        await patientsApi.register(patientData);
        setSuccess(t("patient.registeredSuccessfully"));
        toast.success(t("patient.registeredSuccessfully"));
      }

      setDialogOpen(false);
      resetForm();
      fetchPatients();
    } catch (err) {
      console.error("Failed to save patient:", err);
      setError(err.response?.data?.message || t("patient.failedToSave"));
      toast.error(err.response?.data?.message || t("patient.failedToSave"));
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setPatientForm({
      fullName: "",
      phone: "",
      email: "",
      dateOfBirth: "",
      gender: "",
      address: "",
      isChronic: false,
      chronicCondition: "",
      smsConsent: false,
      emailConsent: false,
      telegramConsent: false,
      telegramUsername: "",
    });
    setSelectedPatient(null);
    setFormErrors({});
  };

  // Open edit dialog
  const handleEdit = (patient) => {
    setSelectedPatient(patient);
    setPatientForm({
      fullName: patient.fullName || "",
      phone: patient.phone || "",
      email: patient.email || "",
      dateOfBirth: patient.dateOfBirth ? patient.dateOfBirth.split('T')[0] : "",
      gender: patient.gender || "",
      address: patient.address || "",
      isChronic: patient.isChronic || false,
      chronicCondition: patient.chronicCondition || "",
      smsConsent: patient.smsConsent || false,
      emailConsent: patient.emailConsent || false,
      telegramConsent: patient.telegramConsent || false,
      telegramUsername: patient.telegramUsername || "",
    });
    setDialogOpen(true);
  };

  // Open view dialog
  const handleView = async (patient) => {
    setSelectedPatient(patient);
    setViewDialogOpen(true);
    fetchPatientHistory(patient.id);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!patientToDelete) return;

    try {
      await patientsApi.delete(patientToDelete.id);
      setSuccess(t("patient.deletedSuccessfully"));
      toast.success(t("patient.deletedSuccessfully"));
      fetchPatients();
    } catch (err) {
      console.error("Failed to delete patient:", err);
      setError(err.response?.data?.message || t("patient.failedToDelete"));
      toast.error(err.response?.data?.message || t("patient.failedToDelete"));
    } finally {
      setDeleteDialogOpen(false);
      setPatientToDelete(null);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (err) {
      return "Invalid date";
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "N/A";
    try {
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch (err) {
      return "N/A";
    }
  };

  // Handle key press for search
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Format gender display
  const formatGender = (genderCode) => {
    if (!genderCode) return 'Not specified';
    switch (genderCode.toUpperCase()) {
      case 'M': return 'Male';
      case 'F': return 'Female';
      case 'OTHER': return 'Other';
      default: return genderCode;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: "bold" }}>
            {t("patient.patientManagement")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("patient.manageRecords")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
        >
          {t("patient.addNewPatient")}
        </Button>
      </Box>

      {/* Error/Success Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Search and Stats Bar */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={t("patient.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={searchLoading}
              >
                {searchLoading ? <CircularProgress size={20} /> : t("common.search")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchPatients}
              >
                {t("common.refresh")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => toast.info("Filter functionality coming soon")}
              >
                {t("common.filter")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "primary.light", mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{statistics.totalPatients}</Typography>
                  <Typography variant="body2" color="text.secondary">{t("patient.totalPatients")}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "secondary.light", mr: 2 }}>
                  <ChronicIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {statistics.chronicPatients}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t("patient.chronicPatients")}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "success.light", mr: 2 }}>
                  <TelegramIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {statistics.withTelegram}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t("patient.telegramUsers")}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "warning.light", mr: 2 }}>
                  <SendIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {statistics.withSmsConsent}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t("patient.smsConsent")}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Patients Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : patientsArray.length === 0 ? (
            <Alert severity="info">
              {t("patient.noPatientsFound")} {searchQuery ? t("patient.tryDifferentSearch") : t("patient.addFirstPatient")}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell>{t("patient.patient")}</TableCell>
                    <TableCell>{t("patient.contact")}</TableCell>
                    <TableCell>{t("patient.ageGender")}</TableCell>
                    <TableCell>{t("patient.healthStatus")}</TableCell>
                    <TableCell>{t("patient.communication")}</TableCell>
                    <TableCell>{t("patient.registered")}</TableCell>
                    <TableCell align="right">{t("patient.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patientsArray.map((patient) => (
                    <TableRow key={patient.id || Math.random()} hover>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ bgcolor: "primary.main", mr: 2, width: 40, height: 40 }}>
                            {patient.fullName?.charAt(0) || 'P'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {patient.fullName || t("patient.unnamedPatient")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t("patient.id")}: {patient.id || t("common.na")}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <PhoneIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                            <Typography variant="body2">{patient.phone || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <EmailIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                            <Typography variant="body2">{patient.email || "N/A"}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip
                            label={`${calculateAge(patient.dateOfBirth)} ${t("patient.yearsShort")}`}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={formatGender(patient.gender)}
                            size="small"
                            color={patient.gender?.toUpperCase() === "M" ? "primary" : patient.gender?.toUpperCase() === "F" ? "secondary" : "default"}
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {patient.isChronic ? (
                            <Chip
                              label={`${t("patient.chronicShort")}: ${patient.chronicCondition || ''}`}
                              size="small"
                              color="warning"
                              variant="outlined"
                              icon={<ChronicIcon />}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ) : (
                            <Chip
                              label={t("patient.healthy")}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {patient.smsConsent && (
                            <Chip
                              label="SMS"
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          )}
                          {patient.emailConsent && (
                            <Chip
                              label="Email"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {patient.telegramConsent && (
                            <Chip
                              label="Telegram"
                              size="small"
                              color="success"
                              variant="outlined"
                              icon={<TelegramIcon />}
                            />
                          )}
                          {!patient.smsConsent && !patient.emailConsent && !patient.telegramConsent && (
                            <Typography variant="caption" color="text.secondary">
                              {t("patient.noConsent")}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(patient.createdAt || patient.registeredAt || patient.createdDate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          <Tooltip title={t("patient.viewDetails")}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleView(patient)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("patient.edit")}>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleEdit(patient)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("patient.delete")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setPatientToDelete(patient);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Floating Action Button for quick add */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: "fixed",
          bottom: 100, // Move up to avoid overlap with chat bot
          right: 32,
        }}
        onClick={() => {
          resetForm();
          setDialogOpen(true);
        }}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Patient Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
            backdropFilter: 'blur(10px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 3,
          background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.05)}, transparent)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
        }}>
          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
            {selectedPatient ? <EditIcon /> : <PersonAddIcon />}
          </Avatar>
          <Typography variant="h6" fontWeight="bold">
            {selectedPatient ? t("patient.updatePatient") : t("patient.registerNewPatient")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`${t("patient.fullName")} *`}
                name="fullName"
                value={patientForm.fullName}
                onChange={handleInputChange}
                error={!!formErrors.fullName}
                helperText={formErrors.fullName}
                margin="normal"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`${t("common.phone")} *`}
                name="phone"
                value={patientForm.phone}
                onChange={handleInputChange}
                error={!!formErrors.phone}
                helperText={formErrors.phone || "Format: +1234567890"}
                margin="normal"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("common.email")}
                name="email"
                type="email"
                value={patientForm.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("common.dateOfBirth")}
                name="dateOfBirth"
                type="date"
                value={patientForm.dateOfBirth}
                onChange={handleInputChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t("common.gender")}
                name="gender"
                value={patientForm.gender}
                onChange={handleInputChange}
                margin="normal"
              >
                <MenuItem value="">{t("common.selectGender")}</MenuItem>
                <MenuItem value="M">{t("common.male")}</MenuItem>
                <MenuItem value="F">{t("common.female")}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("common.address")}
                name="address"
                value={patientForm.address}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={2}
              />
            </Grid>

            {/* Chronic Care Section */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <ChronicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {t("patient.chronicCareInfo")}
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={patientForm.isChronic}
                        onChange={handleInputChange}
                        name="isChronic"
                        color="warning"
                      />
                    }
                    label={t("patient.chronicPatient")}
                  />

                  {patientForm.isChronic && (
                    <TextField
                      fullWidth
                      label={`${t("patient.chronicCondition")} *`}
                      name="chronicCondition"
                      value={patientForm.chronicCondition}
                      onChange={handleInputChange}
                      error={!!formErrors.chronicCondition}
                      helperText={formErrors.chronicCondition || "e.g., HTN, DM, Asthma"}
                      margin="normal"
                      required
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Communication Consent Section */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <SendIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {t("patient.communicationPreferences")}
                  </Typography>

                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={patientForm.smsConsent}
                          onChange={handleInputChange}
                          name="smsConsent"
                        />
                      }
                      label={t("patient.smsConsent")}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={patientForm.emailConsent}
                          onChange={handleInputChange}
                          name="emailConsent"
                        />
                      }
                      label={t("patient.emailConsent")}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={patientForm.telegramConsent}
                          onChange={handleInputChange}
                          name="telegramConsent"
                        />
                      }
                      label={t("patient.telegramConsent")}
                    />

                    {patientForm.telegramConsent && (
                      <TextField
                        fullWidth
                        label={t("patient.telegramUsername")}
                        name="telegramUsername"
                        value={patientForm.telegramUsername}
                        onChange={handleInputChange}
                        error={!!formErrors.telegramUsername}
                        helperText={formErrors.telegramUsername || "Format: @username"}
                        margin="normal"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <TelegramIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </FormGroup>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={loading}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : (selectedPatient ? <CheckCircleIcon /> : <SaveIcon />)}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
              boxShadow: '0 3px 15px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                boxShadow: '0 5px 20px rgba(25, 118, 210, 0.4)',
              }
            }}
          >
            {loading ? t("common.saving") : selectedPatient ? t("patient.updatePatientButton") : t("patient.registerPatientButton")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Patient Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
            backdropFilter: 'blur(10px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }
        }}
      >
        {selectedPatient && (
          <>
            <DialogTitle sx={{
              p: 3,
              background: `linear-gradient(to right, ${alpha(theme.palette.info.main, 0.05)}, transparent)`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
            }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "primary.main", mr: 2, width: 56, height: 56, boxShadow: 2 }}>
                  {selectedPatient.fullName?.charAt(0) || 'P'}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {selectedPatient.fullName || t("patient.unnamedPatient")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("patient.patientId")}: <strong>#{selectedPatient.id || t("common.na")}</strong>
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
                <Tab label={t("patient.profile")} icon={<PersonIcon />} />
                <Tab label={t("patient.prescriptions")} icon={<ReceiptIcon />} />
                <Tab label={t("patient.communication")} icon={<SendIcon />} />
              </Tabs>

              {activeTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
                          {t("patient.personalInfo")}
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText
                              primary={t("common.age")}
                              secondary={`${calculateAge(selectedPatient.dateOfBirth)} ${t("patient.yearsShort")}`}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemText
                              primary={t("common.gender")}
                              secondary={formatGender(selectedPatient.gender)}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemText
                              primary={t("common.dateOfBirth")}
                              secondary={formatDate(selectedPatient.dateOfBirth)}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemText
                              primary={t("patient.chronicPatient")}
                              secondary={selectedPatient.isChronic ? t("common.yes") : t("common.no")}
                            />
                          </ListItem>
                          {selectedPatient.isChronic && (
                            <>
                              <Divider />
                              <ListItem>
                                <ListItemText
                                  primary={t("patient.chronicCondition")}
                                  secondary={selectedPatient.chronicCondition || t("common.na")}
                                />
                              </ListItem>
                            </>
                          )}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
                          {t("patient.contactInfo")}
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: "primary.light", width: 30, height: 30 }}>
                                <PhoneIcon fontSize="small" />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={t("common.phone")}
                              secondary={selectedPatient.phone || t("common.na")}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: "secondary.light", width: 30, height: 30 }}>
                                <EmailIcon fontSize="small" />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={t("common.email")}
                              secondary={selectedPatient.email || t("common.na")}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemText
                              primary={t("common.address")}
                              secondary={selectedPatient.address || t("common.na")}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {activeTab === 2 && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold", display: 'flex', alignItems: 'center' }}>
                      <SendIcon sx={{ mr: 1 }} />
                      {t("patient.communicationPreferences")}
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: selectedPatient.smsConsent ? "info.main" : "grey.300" }}>
                            <SendIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={t("patient.smsConsent")}
                          secondary={selectedPatient.smsConsent ? t("patient.smsConsentGiven") : t("patient.smsConsentNotGiven")}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: selectedPatient.emailConsent ? "primary.main" : "grey.300" }}>
                            <EmailIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={t("patient.emailConsent")}
                          secondary={selectedPatient.emailConsent ? t("patient.emailConsentGiven") : t("patient.emailConsentNotGiven")}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: selectedPatient.telegramConsent ? "success.main" : "grey.300" }}>
                            <TelegramIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={t("patient.telegramConsent")}
                          secondary={
                            selectedPatient.telegramConsent
                              ? `${t("patient.telegramConsentGiven")} ${selectedPatient.telegramUsername ? `(${selectedPatient.telegramUsername})` : ''}`
                              : t("patient.telegramConsentNotGiven")
                          }
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              )}

              {activeTab === 1 && (
                <Box>
                  {historyLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : patientHistory.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("common.date")}</TableCell>
                            <TableCell>{t("common.medicine")}</TableCell>
                            <TableCell>{t("common.dosage")}</TableCell>
                            <TableCell>{t("common.doctor")}</TableCell>
                            <TableCell>{t("common.status")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {patientHistory.map((prescription, index) => (
                            <TableRow key={prescription.id || index}>
                              <TableCell>{formatDate(prescription.createdAt || prescription.date)}</TableCell>
                              <TableCell>{prescription.medicineName || prescription.medicine || t("common.na")}</TableCell>
                              <TableCell>{prescription.dosage || t("common.na")}</TableCell>
                              <TableCell>{prescription.doctorName || prescription.doctor || t("common.na")}</TableCell>
                              <TableCell>
                                <Chip
                                  label={prescription.status ? t(`order.${prescription.status}`) : t("common.unknown")}
                                  size="small"
                                  color={
                                    prescription.status === "fulfilled" ? "success" :
                                      prescription.status === "pending" ? "warning" : "default"
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">
                      {t("patient.noHistoryFound")}
                    </Alert>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 2 }}>
              <Button
                onClick={() => setViewDialogOpen(false)}
                variant="outlined"
                sx={{ borderRadius: 2, px: 3 }}
              >
                {t("common.close")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => {
                  setViewDialogOpen(false);
                  handleEdit(selectedPatient);
                }}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                  boxShadow: '0 3px 15px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 5px 20px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                {t("patient.edit")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
            backdropFilter: 'blur(10px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 3,
          background: `linear-gradient(to right, ${alpha(theme.palette.error.main, 0.05)}, transparent)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
        }}>
          <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main' }}>
            <DeleteIcon />
          </Avatar>
          <Typography variant="h6" fontWeight="bold">{t("patient.confirmDelete")}</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          {patientToDelete && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <span dangerouslySetInnerHTML={{
                __html: t("patient.deleteWarning", { name: patientToDelete.fullName })
              }} />
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              background: `linear-gradient(45deg, ${theme.palette.error.main} 30%, ${theme.palette.error.dark} 90%)`,
              boxShadow: '0 3px 15px rgba(211, 47, 47, 0.3)',
              '&:hover': {
                boxShadow: '0 5px 20px rgba(211, 47, 47, 0.4)',
              }
            }}
          >
            {t("patient.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PatientManagementPage;